/**
 * useAudioPlayback Hook
 *
 * Manages audio playback synchronized with the timeline.
 * Respects trimStart and trimEnd values for audio editing.
 */

import { useEffect, useRef } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { usePlaybackStore } from '@/stores/playbackStore';

// Helper to convert data URL to Blob URL
function dataUrlToBlobUrl(dataUrl: string): string {
  try {
    const byteString = atob(dataUrl.split(',')[1]);
    const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    
    const blob = new Blob([ab], { type: mimeString });
    return URL.createObjectURL(blob);
  } catch (e) {
    console.error('[Audio] Failed to convert data URL to blob URL:', e);
    return dataUrl; // Fallback to original
  }
}

export function useAudioPlayback() {
  const audioTracks = useProjectStore((state) => state.audioTracks);
  const isPlaying = usePlaybackStore((state) => state.isPlaying);
  const currentTime = usePlaybackStore((state) => state.currentTime);
  const masterVolume = usePlaybackStore((state) => state.masterVolume);
  
  const audioElementsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const blobUrlsRef = useRef<Map<string, string>>(new Map());
  
  // Initialize audio elements when tracks change
  useEffect(() => {
    const currentElements = audioElementsRef.current;
    const blobUrls = blobUrlsRef.current;
    
    // Clean up removed tracks
    currentElements.forEach((audio, trackId) => {
      if (!audioTracks.find(t => t.id === trackId)) {
        audio.pause();
        audio.src = '';
        currentElements.delete(trackId);
        
        // Revoke blob URL
        const blobUrl = blobUrls.get(trackId);
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
          blobUrls.delete(trackId);
        }
      }
    });
    
    // Create/update audio elements for each track
    audioTracks.forEach((track) => {
      let audio = currentElements.get(track.id);
      
      if (!audio) {
        // Convert data URL to blob URL for better playback
        const blobUrl = dataUrlToBlobUrl(track.audioData);
        blobUrls.set(track.id, blobUrl);
        
        audio = new Audio();
        audio.src = blobUrl;
        audio.preload = 'auto';
        
        audio.addEventListener('canplaythrough', () => {
          console.log('[Audio] Ready:', track.name);
        });
        
        audio.addEventListener('error', () => {
          console.error('[Audio] Error:', track.name, 'Code:', audio?.error?.code);
        });
        
        currentElements.set(track.id, audio);
      }
      
      audio.volume = track.muted ? 0 : track.volume * masterVolume;
    });
    
    return () => {
      // Cleanup blob URLs on unmount
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
      blobUrls.clear();
    };
  }, [audioTracks, masterVolume]);
  
  // Handle play/pause with trim support
  useEffect(() => {
    const currentElements = audioElementsRef.current;
    
    if (isPlaying) {
      audioTracks.forEach((track) => {
        const audio = currentElements.get(track.id);
        if (!audio) return;
        
        // Calculate effective timeline boundaries accounting for trim
        const effectiveStartTime = track.startTime;
        const effectiveDuration = track.duration - track.trimStart - track.trimEnd;
        const effectiveEndTime = effectiveStartTime + effectiveDuration;
        
        // Check if current time is within the effective (trimmed) track bounds
        if (currentTime >= effectiveStartTime && currentTime < effectiveEndTime) {
          // Calculate position within the trimmed audio
          // timeline position - track start + trim offset = audio element position
          const trackPos = currentTime - effectiveStartTime;
          const audioPos = trackPos + track.trimStart;
          
          if (Math.abs(audio.currentTime - audioPos) > 0.1) {
            audio.currentTime = audioPos;
          }
          
          if (audio.paused && audio.readyState >= 2) {
            audio.play().catch((err) => {
              console.error('[Audio] Play failed:', track.name, err.message);
            });
          }
        } else {
          // Outside trimmed bounds, pause
          if (!audio.paused) {
            audio.pause();
          }
        }
      });
    } else {
      currentElements.forEach((audio) => {
        audio.pause();
      });
    }
  }, [isPlaying, currentTime, audioTracks]);
  
  // Handle seeking/scrubbing when paused
  useEffect(() => {
    if (isPlaying) return; // Only handle when paused, play effect handles during playback
    
    const currentElements = audioElementsRef.current;
    
    audioTracks.forEach((track) => {
      const audio = currentElements.get(track.id);
      if (!audio) return;
      
      // Calculate effective timeline boundaries accounting for trim
      const effectiveStartTime = track.startTime;
      const effectiveDuration = track.duration - track.trimStart - track.trimEnd;
      const effectiveEndTime = effectiveStartTime + effectiveDuration;
      
      // If playhead is within trimmed bounds, sync audio position
      if (currentTime >= effectiveStartTime && currentTime < effectiveEndTime) {
        const trackPos = currentTime - effectiveStartTime;
        const audioPos = trackPos + track.trimStart;
        
        // Only update if significantly different to avoid jitter
        if (Math.abs(audio.currentTime - audioPos) > 0.05) {
          audio.currentTime = audioPos;
        }
      }
    });
  }, [currentTime, audioTracks, isPlaying]);
}

export default useAudioPlayback;
